import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Download, Mail, Phone as PhoneIcon, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LeadStatusSelect } from "@/components/admin/LeadStatusSelect";
import { LeadNotesEditor } from "@/components/admin/LeadNotesEditor";
import { DeleteLeadDialog } from "@/components/admin/DeleteLeadDialog";
import { adminApiClient } from "@/lib/api";
import { SEO } from "@/components/seo/SEO";

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const redirectToLogin = useCallback(() => navigate("/admin"), [navigate]);

  const fetchLeads = useCallback(() => {
    setLoading(true);
    adminApiClient
      .get("/admin/leads", { params: languageFilter !== "all" ? { language: languageFilter } : {} })
      .then((res) => setLeads(res.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          redirectToLogin();
        } else {
          setError("Failed to load leads.");
        }
      })
      .finally(() => setLoading(false));
  }, [languageFilter, redirectToLogin]);

  useEffect(() => {
    // No client-readable token to check (session lives in an httpOnly
    // cookie) -- fetchLeads itself will redirect to /admin on a 401.
    fetchLeads();
  }, [fetchLeads]);

  const handleLogout = async () => {
    try {
      await adminApiClient.post("/admin/logout");
    } catch {
      // Logout should always proceed to the login screen even if the
      // network call fails -- the cookie will simply expire naturally.
    }
    navigate("/admin");
  };

  const handleExport = async () => {
    try {
      const res = await adminApiClient.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "rinnou-leads.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError("Failed to export leads.");
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    setStatusUpdating(leadId);
    try {
      await adminApiClient.patch(`/admin/leads/${leadId}`, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Couldn't update status", { description: "Please try again." });
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleSaveNote = async (leadId, note) => {
    try {
      await adminApiClient.patch(`/admin/leads/${leadId}`, { notes: note });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, notes: note } : l)));
      toast.success("Note saved");
    } catch {
      toast.error("Couldn't save note", { description: "Please try again." });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApiClient.delete(`/admin/leads/${deleteTarget.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success("Consultation request deleted");
      setDeleteTarget(null);
    } catch {
      toast.error("Couldn't delete request", { description: "Please try again." });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SEO title="Leads Dashboard" description="Internal leads dashboard." path="/admin/leads" />
      <div className="min-h-screen bg-[color:var(--paper-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl text-[color:var(--navy-950)]">Leads Dashboard</h1>
              <p className="text-sm text-slate-500 mt-1" data-testid="admin-leads-count">{leads.length} total submissions</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={languageFilter} onValueChange={setLanguageFilter}>
                <SelectTrigger className="w-[160px] bg-white" data-testid="admin-language-filter-select">
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Languages</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleExport} data-testid="admin-export-csv-button">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
              <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-button">
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600 mb-4" data-testid="admin-leads-error">{error}</p>}

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {loading ? (
              <div className="p-16 text-center text-slate-500">Loading leads...</div>
            ) : leads.length === 0 ? (
              <div className="p-16 text-center text-slate-500" data-testid="admin-leads-empty-state">No leads submitted yet.</div>
            ) : (
              <Table data-testid="admin-leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Case Type</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Emailed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id} data-testid={`admin-lead-row-${lead.id}`}>
                      <TableCell className="font-medium text-[color:var(--navy-950)]">{lead.full_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="flex items-center gap-1.5"><PhoneIcon className="h-3 w-3" /> {lead.phone}</span>
                          <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {lead.email}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary">{lead.case_type}</Badge></TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={lead.language === "es" ? "border-[color:var(--gold-500)] text-[color:var(--navy-950)]" : "text-slate-500"}
                          data-testid={`admin-lead-language-${lead.id}`}
                        >
                          {lead.language === "es" ? "Spanish" : "English"}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-slate-600">{lead.message}</TableCell>
                      <TableCell>
                        <LeadStatusSelect
                          leadId={lead.id}
                          status={lead.status || "new"}
                          saving={statusUpdating === lead.id}
                          onChange={handleStatusChange}
                        />
                      </TableCell>
                      <TableCell>
                        <LeadNotesEditor
                          leadId={lead.id}
                          leadName={lead.full_name}
                          notes={lead.notes}
                          onSave={handleSaveNote}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {lead.email_notified ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-slate-400" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTarget(lead)}
                          data-testid={`admin-lead-delete-button-${lead.id}`}
                          aria-label={`Delete request from ${lead.full_name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      <DeleteLeadDialog
        lead={deleteTarget}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
